<template>
  <div class="tw-relative">
    <div
      @mouseenter="() => handleHover()"
      @mouseleave="() => handleHover(true)"
    >
      <!-- top right buttons -->
      <div
        v-if="topButtons && showButtons"
        class="tw-absolute -tw-top-9 sm:-tw-top-12 tw-right-0 tw-flex tw-gap-1"
      >
        <button
          :disabled="!showPrevBtn"
          :class="[
            Classes.TOP_BUTTON,
            btnIconStylesTop,
            { 'tw-pointer-events-auto': showPrevBtn },
            showPrevBtn
              ? 'tw-opacity-100'
              : 'tw-opacity-50 tw-cursor-not-allowed',
          ]"
          @click="scroll(-1)"
        >
          <font-awesome-icon size="sm" icon="chevron-left" />
          <span class="sr-only">{{ $t('previous') }}</span>
        </button>

        <button
          :disabled="!showNextBtn"
          :class="[
            Classes.TOP_BUTTON,
            btnIconStylesTop,
            { 'tw-pointer-events-auto': showNextBtn },
            showNextBtn
              ? 'tw-opacity-100'
              : 'tw-opacity-50 tw-cursor-not-allowed',
          ]"
          @click="scroll(1)"
        >
          <font-awesome-icon size="sm" icon="chevron-right" />
          <span class="sr-only">{{ $t('next') }}</span>
        </button>
      </div>

      <!-- prev btn -->
      <div
        class="tw-left-0"
        :class="[
          navBtnStyles,
          showPrevBtn || pageButtons ? 'tw-opacity-100' : 'tw-opacity-0',
          pageButtons ? 'tw-z-10' : 'tw-z-1',
          {
            'md:tw-w-80': altFade,
          },
        ]"
      >
        <div
          v-if="showFade"
          class="-tw-left-2"
          :class="[
            blackFade
              ? 'mod-gradient-lr--black'
              : altFade
                ? 'mod-gradient-theme-1-lr--dark-3'
                : 'mod-gradient-theme-1-lr--dark',
            gradientStyles,
          ]"
        />
        <button
          v-if="showButtons && !topButtons && (!pageButtons || pageCount > 1)"
          class="tw-left-0.5"
          :tabIndex="pageButtons ? -1 : 0"
          :class="[
            btnIconStyles,
            pageButtons
              ? [
                  '!tw-size-12 tw-opacity-0',
                  isFrontend ? 'lg:-tw-left-6' : 'lg:tw-left-0',
                ]
              : 'tw-left-0.5',
            {
              'tw-pointer-events-auto': showPrevBtn && !pageButtons,
              'lg:tw-opacity-100 lg:tw-pointer-events-auto':
                pageButtons && isHovering,
            },
          ]"
          @click="
            pageButtons
              ? scrollToPageIndex(
                  currentIndex - 1 >= 0 ? currentIndex - 1 : pageCount - 1,
                  false
                )
              : scroll(-1)
          "
        >
          <font-awesome-icon
            :size="pageButtons ? 'lg' : undefined"
            icon="chevron-left"
          />
          <span class="sr-only">{{ $t('previous') }}</span>
        </button>
      </div>

      <!-- slider track -->
      <div
        ref="target"
        :class="[
          pageButtons ? 'tw-overflow-x-hidden' : 'tw-overflow-x-auto',
          !pageButtons &&
            (showPrevBtn || showNextBtn) &&
            (isDragging ? 'tw-cursor-grabbing' : 'tw-cursor-grab'),
        ]"
        :style="{ 'scrollbar-width': showScrollbar ? null : 'none' }"
        @touchstart="(touchEvent) => handleTouchStart(touchEvent)"
        @touchend="(touchEvent) => handleSwipe(touchEvent)"
      >
        <div :class="{ 'tw-pointer-events-none': isDragging }">
          <slot />
        </div>
      </div>

      <!-- next btn -->
      <div
        class="tw-right-0"
        :class="[
          navBtnStyles,
          showNextBtn || pageButtons ? 'tw-opacity-100' : 'tw-opacity-0',
          pageButtons ? 'tw-z-10' : 'tw-z-1',
          {
            'md:tw-w-80': altFade,
          },
        ]"
      >
        <div
          v-if="showFade"
          class="-tw-right-2"
          :class="[
            blackFade
              ? 'mod-gradient-rl--black'
              : altFade
                ? 'mod-gradient-theme-1-rl--dark-3'
                : 'mod-gradient-theme-1-rl--dark',
            gradientStyles,
          ]"
        />

        <button
          v-if="showButtons && !topButtons && (!pageButtons || pageCount > 1)"
          :tabindex="pageButtons ? -1 : 0"
          :class="[
            btnIconStyles,
            pageButtons
              ? [
                  '!tw-size-12 tw-opacity-0',
                  isFrontend ? 'lg:-tw-right-6' : 'lg:tw-right-0',
                ]
              : 'tw-right-0.5',
            {
              'tw-pointer-events-auto': showNextBtn && !pageButtons,
              'lg:tw-opacity-100 lg:tw-pointer-events-auto':
                pageButtons && isHovering,
            },
          ]"
          @click="
            pageButtons
              ? scrollToPageIndex(
                  currentIndex < pageCount - 1 ? currentIndex + 1 : 0,
                  false
                )
              : scroll(1)
          "
        >
          <font-awesome-icon
            :size="pageButtons ? 'lg' : undefined"
            icon="chevron-right"
          />
          <span class="sr-only">{{ $t('next') }}</span>
        </button>
      </div>
    </div>

    <!-- page buttons -->
    <div
      v-if="pageButtons && pageCount > 1"
      class="tw-absolute -tw-bottom-12 tw-w-full tw-flex tw-justify-center tw-gap-x-3 md:tw-gap-x-2"
    >
      <button
        v-for="(_, index) in pageCount"
        :key="index"
        class="tw-size-6 md:tw-size-11"
        :class="Classes.PAGE_BUTTON"
        @click="scrollToPageIndex(index)"
      >
        <span class="sr-only">{{ $t('page') }} {{ index + 1 }}</span>
        <div class="tw-w-6 md:tw-w-11 tw-h-1 tw-relative tw-overflow-hidden">
          <span
            class="tw-absolute tw-w-full tw-inset-y-0 tw-left-0 tw-z-1"
            :class="
              currentIndex === index
                ? [
                    'tw-opacity-50',
                    isFrontend ? 'tw-bg-primary' : 'game-primary-button-color',
                  ]
                : [
                    'tw-opacity-30',
                    isFrontend
                      ? 'tw-bg-dark-text'
                      : 'game-tile-background-color',
                  ]
            "
          />
          <span
            v-if="currentIndex === index"
            ref="fillEl"
            class="tw-absolute tw-inset-y-0 tw-left-0 tw-w-0 tw-z-2"
            :class="isFrontend ? 'tw-bg-primary' : 'game-primary-button-color'"
          />
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import { secsToMs, clamp } from '@helpers/utils.js'
import Classes from '@shared/js/config/css.js'
import { isFrontend } from '@config'
import {
  useResizeObserver,
  useEventListener,
  useElementSize,
  useScroll,
} from '@vueuse/core'
import {
  onUnmounted,
  onMounted,
  computed,
  nextTick,
  toRefs,
  watch,
  ref,
} from 'vue'

const DECEL_RATE = 0.96
const MIN_INERTIA_AMOUNT = 1
const INERTIA_SCALE = 8
const INERTIA_STOP_AMOUNT = 0.1
const MAX_VISIBLE_PCT = 0.97
const HIDE_BUTTON__TOLERANCE = 5
const MIN_MOVE_THRESHOLD = 3
const MIN_SWIPE_X_DIST = 10
const MAX_SWIPE_Y_DIST = 40

const navBtnStyles =
  'tw-flex tw-items-center tw-h-full tw-w-10 xs:tw-w-20 tw-pointer-events-none tw-absolute tw-top-0 tw-transition-opacity'
const gradientStyles = 'tw-absolute tw-top-0 tw-size-full'
const btnIconStyleShared =
  'tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full mod-button-transition tw-absolute tw-top-1/2 tw--translate-y-1/2'
const btnIconStyles = `game-primary-button-color game-primary-text hover:game-primary-button-color-hover focus:game-primary-button-color-hover hover:game-primary-text-hover focus:game-primary-text-hover ${btnIconStyleShared}`
const btnIconStylesTop = `tw-bg-dark-3 hover:tw-bg-dark-2 focus:tw-bg-dark-3 tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full mod-button-transition`

const autoScrollMS = secsToMs(6)

export default {
  props: {
    blackFade: {
      type: Boolean,
      default: false,
    },
    showFade: {
      type: Boolean,
      default: true,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    topButtons: {
      type: Boolean,
      default: false,
    },
    pageButtons: {
      type: Boolean,
      default: false,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    showScrollbar: {
      type: Boolean,
      default: true,
    },
    startEnd: {
      type: Boolean,
      default: false,
    },
    altFade: {
      type: Boolean,
      default: false,
    },
    container: {
      type: HTMLElement,
      default: null,
    },
  },
  setup(props) {
    const {
      container: containerRef,
      pageButtons,
      pageCount,
      startEnd,
    } = toRefs(props)

    const isDragging = ref(false)
    const isHovering = ref(false)
    const currentIndex = ref(0)
    const scrollWidth = ref(0)
    const target = ref(null)
    const fillEl = ref(null)

    let lastMousePos = { x: 0, y: 0 }
    let autoScrollTimeoutId = null
    let fillAnimation = null
    let isMouseDown = false
    let startScrollPos = 0
    let mouseDownPos = 0
    let lastTime = 0
    let inertia = 0

    const { width: elemWidth } = useElementSize(target)
    const { x: scrollX } = useScroll(target)

    const showPrevBtn = computed(() => scrollX.value > HIDE_BUTTON__TOLERANCE)
    const showNextBtn = computed(
      () =>
        scrollWidth.value - elemWidth.value > 10 &&
        scrollX.value <
          scrollWidth.value - elemWidth.value - HIDE_BUTTON__TOLERANCE
    )

    useResizeObserver(target, () => {
      scrollWidth.value = target.value?.scrollWidth || 0
      if (pageButtons.value && target.value) {
        // anchor to the new page instantly so resizing the view doesn't leave the viewport landing mid-card.
        target.value.scrollLeft = pageScrollTarget(currentIndex.value)
      }
    })

    useEventListener(target, 'mousedown', (e) => {
      if (pageButtons.value) return

      isMouseDown = true
      mouseDownPos = e.clientX
      lastMousePos = { x: e.clientX, y: e.clientY }
      startScrollPos = scrollX.value
      lastTime = performance.now()
      e.preventDefault()
    })

    useEventListener('mouseup', () => {
      isMouseDown = false
      isDragging.value = false

      if (Math.abs(inertia) > MIN_INERTIA_AMOUNT) {
        requestAnimationFrame(inertiaScroll)
      }
    })

    useEventListener('mousemove', (e) => {
      if (!isMouseDown) return

      // Include a small threshold to prevent accidental drags when clicking
      isDragging.value =
        Math.abs(mouseDownPos - e.clientX) >= MIN_MOVE_THRESHOLD
      scrollX.value = startScrollPos + mouseDownPos - e.clientX

      const thisTime = performance.now()
      inertia = (lastMousePos.x - e.clientX) / (thisTime - lastTime)

      lastMousePos = { x: e.clientX, y: e.clientY }
      lastTime = thisTime
    })

    onMounted(() => {
      if (startEnd.value) {
        scrollX.value = 100000
      }
    })

    if (pageButtons.value) {
      watch(
        pageCount,
        (count) => {
          if (count > 0) nextTick(startAutoScroll)
        },
        { immediate: true }
      )
    }

    function inertiaScroll() {
      if (isMouseDown) return

      if (Math.abs(inertia) > 0) {
        scrollX.value += inertia * INERTIA_SCALE
        inertia *= DECEL_RATE
      }

      if (Math.abs(inertia) > INERTIA_STOP_AMOUNT) {
        requestAnimationFrame(inertiaScroll)
      }
    }

    function scroll(dir) {
      inertia = 0

      const container =
        containerRef.value || target.value.children[0].children[0]
      const viewport = target.value.clientWidth

      const childCount = container.childElementCount
      const childWidth = container.children[0].clientWidth

      const gapSize =
        (container.scrollWidth - childCount * childWidth) / (childCount - 1)

      const { left: targetLeftEdge, right: targetRightEdge } =
        target.value.getBoundingClientRect()

      let leftmostVisibleIndex = -1
      let rightmostVisibleIndex = -1

      // Find the percentage that each tile is in the viewport
      const pctInView = [...container.children].map((elem, i) => {
        const { left: elemLeftEdge, right: elemRightEdge } =
          elem.getBoundingClientRect()

        const leftOffset = elemLeftEdge - targetLeftEdge
        const rightOffset = elemRightEdge - targetRightEdge
        let pct

        if (leftOffset < 0) {
          pct = clamp((leftOffset + childWidth) / childWidth, 0, 1)
        } else if (rightOffset > 0) {
          pct = clamp((childWidth - rightOffset) / childWidth, 0, 1)
        } else {
          pct = 1
        }

        if (pct > MAX_VISIBLE_PCT) {
          if (leftmostVisibleIndex === -1) leftmostVisibleIndex = i
          rightmostVisibleIndex = i
        }

        return pct
      })

      // Find how many tiles can fit within the viewport
      let maxTilesInView = Math.floor(viewport / childWidth)
      if (
        viewport -
          maxTilesInView * childWidth -
          (maxTilesInView - 1) * gapSize <
        0
      ) {
        maxTilesInView--
      }

      // Find the tile that is most on-screen
      let closestIndex = -1
      let maxPct = 0
      pctInView.forEach((pct, i) => {
        if (pct > maxPct) {
          closestIndex = i
          maxPct = pct
        }
      })

      if (leftmostVisibleIndex === -1) {
        if (pctInView[closestIndex + dir] === 0) {
          closestIndex -= dir
        }
        leftmostVisibleIndex = closestIndex
        rightmostVisibleIndex = closestIndex
      }

      // nextIndex is the index of the next leftmost tile
      const nextIndex =
        (dir > 0 ? rightmostVisibleIndex : leftmostVisibleIndex) +
        dir * (dir > 0 ? 1 : maxTilesInView)

      const scrollLeft =
        nextIndex * (childWidth + gapSize) -
        (viewport -
          maxTilesInView * childWidth -
          (maxTilesInView - 1) * gapSize) /
          2

      _scrollTo(scrollLeft)
    }

    function pageScrollTarget(index) {
      if (!target.value || pageCount.value < 1) return 0

      const container =
        containerRef.value || target.value.children[0].children[0]

      if (!container?.children.length) return 0

      const totalItems = container.childElementCount

      const cardIndex = clamp(
        index * Math.ceil(totalItems / pageCount.value),
        0,
        totalItems - 1
      )

      if (!container.children[cardIndex]) return 0

      return (
        target.value.scrollLeft +
        (container.children[cardIndex].getBoundingClientRect().left -
          target.value.getBoundingClientRect().left)
      )
    }

    function _scrollTo(scrollLeft) {
      const maxLeft = Math.max(
        0,
        target.value.scrollWidth - target.value.clientWidth
      )

      target.value.scrollTo({
        left: clamp(scrollLeft, 0, maxLeft),
        behavior: 'smooth',
      })
    }

    function scrollToPageIndex(index, autoScroll = true) {
      if (index === currentIndex.value) return
      currentIndex.value = index
      _scrollTo(pageScrollTarget(index))
      if (autoScroll) startAutoScroll()
    }

    async function startLoadingFill() {
      await nextTick()
      const el = fillEl.value?.[0]
      if (!el) return

      if (fillAnimation) fillAnimation.cancel()
      fillAnimation = el.animate([{ width: '0%' }, { width: '100%' }], {
        duration: autoScrollMS,
        easing: 'linear',
        fill: 'forwards',
      })
    }

    function startAutoScroll() {
      if (autoScrollTimeoutId !== null) clearTimeout(autoScrollTimeoutId)
      startLoadingFill()
      autoScrollTimeoutId = setTimeout(() => {
        const nextIndex =
          currentIndex.value === Math.ceil(pageCount.value) - 1
            ? 0
            : currentIndex.value + 1
        scrollToPageIndex(nextIndex)
      }, autoScrollMS)
    }

    function pauseAutoScroll() {
      if (autoScrollTimeoutId !== null) {
        clearTimeout(autoScrollTimeoutId)
        autoScrollTimeoutId = null
      }
      if (fillAnimation) fillAnimation.cancel()
    }

    onUnmounted(() => {
      if (autoScrollTimeoutId !== null) clearTimeout(autoScrollTimeoutId)
    })

    function handleHover(leave = false) {
      if (!pageButtons.value) return

      isHovering.value = !leave
      if (leave) startAutoScroll()
      else pauseAutoScroll()
    }

    function handleSwipe(touchEvent) {
      if (!pageButtons.value) return

      const touch = touchEvent.changedTouches[0]
      if (!touch) return

      const deltaX = Math.abs(touch.clientX - lastMousePos.x)
      const deltaY = Math.abs(touch.clientY - lastMousePos.y)

      if (deltaY > MAX_SWIPE_Y_DIST) return

      if (deltaX > MIN_SWIPE_X_DIST) {
        const direction = Math.sign(lastMousePos.x - touch.clientX)

        scrollToPageIndex(
          (currentIndex.value + direction + pageCount.value) % pageCount.value
        )

        startAutoScroll()
        lastMousePos = { x: touch.clientX, y: touch.clientY }
      }
    }

    function handleTouchStart(touchEvent) {
      if (!pageButtons.value) return

      lastMousePos = {
        x: touchEvent.touches[0]?.clientX || 0,
        y: touchEvent.touches[0]?.clientY || 0,
      }
    }

    return {
      scrollToPageIndex,
      handleTouchStart,
      btnIconStylesTop,
      gradientStyles,
      btnIconStyles,
      currentIndex,
      navBtnStyles,
      handleSwipe,
      handleHover,
      showNextBtn,
      showPrevBtn,
      isFrontend,
      isDragging,
      isHovering,
      Classes,
      fillEl,
      scroll,
      target,
    }
  },
}
</script>
